import { useEffect, useState } from "react";
import { api } from "../../../../lib/api";
import { ledgerGroupLabels } from "./constants";
import SearchableSelect from "./SearchableSelect";

/**
 * Unified, SEARCHABLE account picker for the multi-line grid (Journal,
 * Vendor Bill, Credit/Debit Note, generic Draft editing) and for the
 * operational Payment/Receipt screens' non-bank side - the "advanced"
 * screens where Phase 5 explicitly allows raw ledger names (see the Journal
 * exception), but where a Bank Account or Vendor should still be offered as
 * its own first-class, human-readable option rather than forcing the user
 * back to a bare ledger name for those cases.
 *
 * Phase 5 "Searchable Selectors" correction: replaced the old plain <Select>
 * (a long unfiltered dropdown) with the SearchableSelect combobox, grouped
 * by category (Cash / Bank Accounts / Vendors / one group per ledger type -
 * "Expenses", "Vendors / Payables", "Assets", etc.) so typing "sec" finds
 * "Security salary expense" immediately instead of requiring a scroll
 * through the whole Chart of Accounts.
 *
 * The optional `context` prop filters which raw ledger groups appear in the
 * "Other Ledgers" buckets, per the "Filter Results by Voucher Context"
 * correction - e.g. Payment's debit side should not casually offer Income
 * ledgers or arbitrary Bank ledgers. Callers that want the full,
 * unrestricted Chart of Accounts (Journal Voucher, generic Draft Edit - the
 * sanctioned "advanced" screens) simply omit `context`.
 *
 * Encodes the selection as "ledger:<id>" | "bank:<id>" | "vendor:<id>" so a
 * caller can build a voucher line with exactly one of ledgerId/bankAccountId
 * /vendorId set - never more than one, and never a value invented by the
 * browser. Decode with decodeAccountSelection() before submitting.
 *
 * To avoid showing the same real-world account twice (once as "Main Bank"
 * the raw ledger, once as "IDBI — ... — ****9644" the bank account), any
 * ledger already represented by an active Bank Account or Vendor is left out
 * of the plain "Other Ledgers" group - it is only offered through its own
 * Bank Account / Vendor entry.
 */
export function decodeAccountSelection(value) {
  if (!value) return null;
  const sep = value.indexOf(":");
  if (sep < 0) return null;
  const kind = value.slice(0, sep);
  const id = value.slice(sep + 1);
  if (kind === "bank") return { bankAccountId: id };
  if (kind === "vendor") return { vendorId: id };
  if (kind === "ledger") return { ledgerId: id };
  return null;
}

export function encodeLedgerSelection(ledgerId) { return `ledger:${ledgerId}`; }
export function encodeBankSelection(bankAccountId) { return `bank:${bankAccountId}`; }
export function encodeVendorSelection(vendorId) { return `vendor:${vendorId}`; }

/** Reusable data loader shared by AccountSelect and the line grid, so the
 * grid can resolve a stored line's ledgerId/bankAccountId/vendorId back into
 * the right encoded value when opening an existing draft for editing. */
export function useAccountOptions() {
  const [ledgers, setLedgers] = useState([]);
  const [bankAccounts, setBankAccounts] = useState([]);
  const [vendors, setVendors] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      try {
        const [ledgersRes, bankRes, vendorRes] = await Promise.all([
          api.get("/society/accounting/engine/ledgers?annotate=true").catch(() => ({ data: [] })),
          api.get("/society/accounting/engine/bank-accounts").catch(() => ({ data: [] })),
          api.get("/society/accounting/engine/vendors").catch(() => ({ data: [] })),
        ]);
        setLedgers(Array.isArray(ledgersRes.data) ? ledgersRes.data : []);
        setBankAccounts(Array.isArray(bankRes.data) ? bankRes.data : []);
        setVendors(Array.isArray(vendorRes.data) ? vendorRes.data : []);
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  const otherLedgers = ledgers.filter((l) => {
    if (l.ledgerGroup === "cash") return false;
    if (String(l.linkedEntity || "").startsWith("Bank Account:")) return false;
    if (String(l.linkedEntity || "").startsWith("Vendor:")) return false;
    // Phase 13 (Journal/Contra correction), item 1: every new Society is
    // seeded with a generic system "Main bank" ledger (see
    // SOCIETY_DEFAULT_LEDGERS) from before the Bank Account master existed.
    // Once a real, properly-labeled Bank Account is set up (its own linked
    // ledger already hidden above), that placeholder becomes a second,
    // confusing "bank" entry with no real identity of its own - hide it from
    // selection then, exactly like a Bank Account's auto-linked ledger is
    // hidden. Never hidden before any real Bank Account exists, since it
    // would then be the Society's only bank option and hiding it would leave
    // no way to record a bank transaction at all.
    if (l.isSystem && l.ledgerGroup === "bank" && bankAccounts.length > 0) return false;
    return true;
  });
  const cashLedgers = ledgers.filter((l) => l.ledgerGroup === "cash");

  return { ledgers, bankAccounts, vendors, cashLedgers, otherLedgers, loading };
}

// Which raw ledger groups a non-Journal operational screen's "Other Ledgers"
// bucket may show. Vendors/Bank Accounts/Cash are handled through their own
// dedicated groups above and are unaffected by this filter. Omitting
// `context` entirely (Journal, generic Draft Edit) skips this filter and
// shows every ledger group - the deliberate Journal exception.
const CONTEXT_ALLOWED_LEDGER_GROUPS = {
  // Payment Voucher's "Expense / Payable Account" side: Expenses, Assets,
  // Liabilities and raw vendor-group ledgers are valid debit targets. Income,
  // Bank, Cash, Fund and Equity/opening-adjustment ledgers are excluded -
  // paying "into" Income or an arbitrary Bank ledger from this field would
  // be a bookkeeping error, not a real Payment.
  paymentDebit: new Set(["expense", "asset", "liability", "vendor", "member"]),
  // Payment Voucher's "Direct Expense" nature: a cost being incurred and paid
  // in the same step, with no previously-booked liability involved at all.
  // ONLY plain Expense ledgers belong here - never Asset (that's now its own
  // "Asset / Capital Payment" nature), Vendor, Liability or Member groups,
  // which would silently smuggle a payable back in even though the user
  // explicitly said "no payable exists" by choosing this Payment Nature.
  // See the Phase 5 "Enforce Payment Nature" correction.
  paymentDirectExpense: new Set(["expense"]),
  // Payment Voucher's "Payable Payment" nature: an existing Vendor/Payable
  // liability being cleared. Vendor entities (hideVendors left false by the
  // caller) plus raw vendor/liability-group ledgers - never Expense, since
  // that would mean no liability actually exists.
  paymentPayable: new Set(["vendor", "liability"]),
  // Payment Voucher's "Advance Payment" nature: money paid out ahead of a
  // bill/service, tracked as a recoverable Asset until it is adjusted later.
  // Phase 15 (Vendor Bills & Payables) item 4: "vendor" added so a real
  // Vendor can be selected here too - the advance posts Dr that Vendor's OWN
  // dedicated ledger (see BankVoucherFormPage, hideVendors removed for this
  // nature), which nets automatically against a later Vendor Bill posted to
  // the same ledger, with no separate "apply advance" step needed.
  paymentAdvance: new Set(["asset", "vendor"]),
  // Payment Voucher's "Asset / Capital Payment" nature: a capital purchase
  // (Fixed Deposit, Investment, Equipment), not a routine running expense.
  paymentCapital: new Set(["asset"]),
  // Payment Voucher's "Refund" nature: repaying money previously received
  // from a Member, Vendor, or against a Liability/Advance already on the
  // books - never Income/Bank/Cash/Equity, which a refund can never touch.
  paymentRefund: new Set(["asset", "liability", "vendor", "member"]),
  // Receipt Voucher's "Income / Receivable Account" side: Income, Assets
  // (receivables), Liabilities (e.g. clearing a member advance) and
  // Equity/capital contributions are valid credit targets. Expense, Bank,
  // Cash, Fund and raw vendor-payable ledgers are excluded. Kept as-is for
  // Receipt Type "Other Receipt" (see the Phase 12 Receipt Type correction
  // below - the other three Receipt Types now get their own, narrower
  // context instead of sharing this one).
  receiptCredit: new Set(["income", "asset", "liability", "member", "equity"]),
  // Receipt Type "Vendor Refund": money coming back from a Vendor - either
  // the Vendor entity itself (hideVendors left false by the caller) or a
  // raw ledger representing the liability/advance/expense being reduced.
  // Never Income/Bank/Cash/Member/Equity, which a vendor refund can never
  // touch.
  receiptVendorRefund: new Set(["vendor", "liability", "asset", "expense"]),
  // Receipt Type "Income Receipt": strictly Income-group ledgers (Bank
  // Interest, Hall Income, Miscellaneous Income, etc.) - never a
  // receivable/liability/member ledger, which would misclassify a real
  // receivable settlement as fresh income.
  receiptIncome: new Set(["income"]),
  // Phase 13 (Journal/Contra correction), item 6: Journal Voucher is for
  // non-cash ledger adjustments, not a back door into control/subledger
  // accounts that are only meaningfully tracked per member or per vendor.
  // "vendor" (shared control payables like Watchman/Sweeper Payable - see
  // SOCIETY_DEFAULT_LEDGERS) and "member" (Member Receivables/Advances) are
  // excluded here for that reason; a genuine vendor or member adjustment
  // belongs in Payment/Receipt, where the party-aware outstanding panel
  // keeps the subledger and its control account in agreement. Bank and Cash
  // are excluded too (item 2 - "prefer excluding Bank and Cash from normal
  // Journal selection"); GridVoucherFormPage.jsx separately offers an
  // explicit "include Bank/Cash" opt-in for a genuine adjustment, which
  // falls back to the ordinary deduped Bank Account/Cash pickers rather than
  // this context, so no raw duplicate ever appears either way.
  journalAdjustment: new Set(["income", "expense", "asset", "liability", "fund", "equity", "other"]),
  // Same as journalAdjustment, but for when the user has explicitly opted
  // in to including Bank/Cash for a genuine adjustment (see
  // GridVoucherFormPage.jsx's "allowBankCash" toggle) - "vendor" and
  // "member" (subledger control accounts) stay excluded either way, since
  // that exclusion is never conditional.
  journalAdjustmentWithBankCash: new Set(["income", "expense", "asset", "liability", "fund", "equity", "other", "bank", "cash"]),
  // Phase 15 CORRECTION (Tally-like Purchase / Vendor Bill), item 2: the
  // allocation-row ledger picker on a Vendor Bill may only offer Expense
  // ledgers (Repairs & Maintenance, Security Expense, Electricity Expense,
  // Professional Fees, Housekeeping, Other Expenses, ...) and Asset-group
  // ledgers (covers both Fixed Assets - Furniture, Computers, Equipment,
  // Machinery - and Purchase/Consumables/Stores, which this system also
  // books to the "asset" ledger group since there is no finer built-in
  // sub-classification). Bank, Cash, Vendor, Member, Income, Liability,
  // Fund and Equity/opening ledgers are never offered here - a Vendor Bill
  // books an expense/asset against a payable, never anything else.
  purchaseAllocation: new Set(["expense", "asset"]),
};

export default function AccountSelect({
  value, onChange, placeholder = "Select an account", searchPlaceholder = "Search ledger, vendor or account...",
  disabled, options, hideCash = false, hideBankAccounts = false, hideVendors = false, context, clearable = false,
}) {
  const owned = useAccountOptions();
  const { cashLedgers: allCash, bankAccounts: allBank, vendors: allVendors, otherLedgers: allOther, loading } = options || owned;
  const cashLedgers = hideCash ? [] : allCash;
  const bankAccounts = hideBankAccounts ? [] : allBank;
  const vendors = hideVendors ? [] : allVendors;
  const allowedGroups = context ? CONTEXT_ALLOWED_LEDGER_GROUPS[context] : null;
  // JOURNAL LEDGER CONTROL correction, item 1: "Opening balance adjustment"/
  // "Opening Balance Equity" is a system control account fed exclusively by
  // Bank Account/Vendor/Fund/Member opening-balance entry and Financial Year
  // carry-forward (see _get_or_create_opening_balance_equity_ledger on the
  // backend) - a manual Journal posting against it would double-count what
  // those flows already post directly. Scoped to the two Journal contexts
  // only, per "Do not change anything else" - every other screen that can
  // reach the "equity" ledger group (e.g. Receipt's Other Receipt context)
  // is unaffected.
  const isJournalContext = context === "journalAdjustment" || context === "journalAdjustmentWithBankCash";
  const otherLedgers = (allowedGroups ? allOther.filter((l) => allowedGroups.has(l.ledgerGroup)) : allOther)
    .filter((l) => !(isJournalContext && l.isOpeningBalanceLedger));

  const comboOptions = [
    ...cashLedgers.map((l) => ({ value: encodeLedgerSelection(l.id), label: l.name, group: "Cash", keywords: ["cash"] })),
    ...bankAccounts.map((a) => ({
      value: encodeBankSelection(a.id),
      label: [a.bankName, a.accountName, a.accountNumber].filter(Boolean).join(" — "),
      group: "Bank Accounts",
      keywords: [a.bankName, a.branch, a.accountName, a.accountNumber],
    })),
    ...vendors.map((v) => ({
      value: encodeVendorSelection(v.id),
      label: v.vendorName,
      sublabel: [v.contactPerson, v.mobile, v.gstin].filter(Boolean).join(" · ") || undefined,
      group: "Vendors",
      keywords: [v.contactPerson, v.mobile, v.gstin],
    })),
    ...otherLedgers.map((l) => ({
      value: encodeLedgerSelection(l.id),
      label: l.name,
      sublabel: l.linkedEntity || undefined,
      group: ledgerGroupLabels[l.ledgerGroup] || "Other Ledgers",
      keywords: [l.code],
    })),
  ];

  return (
    <SearchableSelect
      value={value}
      onChange={onChange}
      options={comboOptions}
      loading={loading}
      disabled={disabled}
      placeholder={placeholder}
      searchPlaceholder={searchPlaceholder}
      emptyText="No matching accounts."
      clearable={clearable}
    />
  );
}
